import { LargeTitle, makeStyles, mergeClasses, Text } from "@fluentui/react-components";
import { mergeStyles } from '@fluentui/merge-styles';
import IHeroProps, { ICLientLogo } from "./IHeroProps";
import { useTranslation } from "react-i18next";
import { ScrollDownButton } from "./ScrollDownButton";
import CallsToAction from "./CallToAction";

const useStyles = makeStyles({
    hero: {
        position: "relative",
        height: "100vh",
        backgroundPosition: "center",
        backgroundSize: "cover",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        overflow: "hidden",
    },
    heroOverlay: {
        position: "relative",
        
        '&::before': {
            content: "''",
            position: "absolute",
            inset: 0,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(40%)",
            zIndex: 0,
        }
    },
    videoPlayer: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        position: "absolute",
        inset: 0,
        zIndex: 0,
        filter: "brightness(40%)"
    },
    videoContainer: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        overflow: "hidden",
        pointerEvents: "none",
    },
    backgroundVideo: {
        width: "100vw",
        height: "100vh",
        objectFit: "cover",
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 0,
        pointerEvents: "none",
    },
    heroContentWrapper: {
        zIndex: 2,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "94vh",
        position: "relative",

        "@media (max-width: 900px)": {
            flexDirection: "column",
            height: "auto",
        },
    },
    content: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "2rem 3rem",
        paddingLeft: "0rem",
        color: "white",
        maxWidth: "700px",
        zIndex: 2,
        
        "@media (max-width: 900px)": {
            alignItems: "center",
            padding: "2rem 1rem",
            maxWidth: "100%",
            borderLeft: "none",
            textAlign: "center",
        },
    },
    title: {
        color: "white",
        textAlign: "left",
        width: "100%",
        fontSize: "4em",
        lineHeight: "1.25em",
        marginBottom: "1rem",

        "@media (max-width: 900px)": {
            textAlign: "center",
            fontSize: "3em",
        },
    },
    description: {
        color: "white",
        textAlign: "left",
        maxWidth: "800px",
        marginBottom: "1.5rem",
        width: "100%",

        "@media (max-width: 900px)": {
            textAlign: "center",
        }
    },
    badge: {
        display: "inline-block",
        padding: "0.4rem 0.8rem",
        backgroundColor: "transparent",
        color: "white",
        borderRadius: '0.5rem !important',
        border: `none`,
        fontSize: "12px",
        fontWeight: "var(--fontWeightSemibold)",
        textTransform: "uppercase",
        letterSpacing: "0.5px"
    },
    logoList: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "0.5rem",
        marginRight: "2rem",
        minWidth: "120px",
        position: "relative",
        paddingTop: "1rem",
        borderRight: "1px solid white",
        paddingRight: "2rem"
    },
    logoBadge: {
        marginBottom: "0.5rem",
        border: "1px white solid",
        borderRadius: "2rem",
        fontSize: "1rem",
        fontWeight: 300,
        letterSpacing: "0.05em",
        zIndex: 2,
        alignSelf: "center",
        display: "inline-block",
        padding: "0.4rem 0.8rem",
        backgroundColor: "transparent",
        color: "white"
    },
    logoColumn: {
        display: "flex",
        flexDirection: "column",
        gap: "0.25em",
        alignItems: "center"
    },
    logoItem: {
        width: "120px",
        height: "60px",
        borderRadius: "0.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0rem"
    },
    logoItemHelpdesk: {
        width: "150px",
        height: "80px",
    },
    logoImg: {
        maxWidth: "75%",
        maxHeight: "75%",
        objectFit: "contain",
        display: "block"
    },
    logoImgHelpdesk: {
        maxHeight: "100%",
    },
    heroRow: {
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        width: "100%",
        justifyContent: "center",
        gap: "2rem"
    },
    clientLogos: {
        "@media (max-width: 900px)": {
            display: "none",
        }
    },
    ctaContainer: {
        display: "flex",
        flexDirection: "row",
        gap: "var(--spacing-horizontal-m)",
        alignItems: "center",
        "@media (max-width: 768px)": {
            flexDirection: "column",
            gap: "var(--spacing-vertical-m)",
            alignItems: "stretch"
        }
    }
});

const HeroClientLogoList = ({ logos }: { logos: ICLientLogo[] }) => {
    const { t } = useTranslation("home");
    const styles = useStyles();

    if (!logos || logos.length === 0) {
        return null;
    }

    // Check if support site and adjust image height accordingly
    const isSupportSite = (process.env.REACT_APP_IS_SUPPORT_SITE || "").toLowerCase() === "true";

    return (
        <div className={styles.logoList}>
            <span className={styles.logoBadge}>{t("hero.trustedBy")}</span>
            <div className={styles.logoColumn}>
                {
                    logos.map((logo) => (
                        <a
                            href={logo.url}
                            key={logo.src}
                            className={mergeClasses(styles.logoItem, isSupportSite && styles.logoItemHelpdesk)}
                        >
                            <img
                                src={logo.src}
                                alt={logo.alt}
                                loading="lazy"
                                className={mergeClasses(styles.logoImg, isSupportSite && styles.logoImgHelpdesk)}
                            />
                        </a>
                    ))
                }
            </div>
        </div>
    );
};

// logic for full page scroll
function scrollToId(id?: string) {
    if (!id) {
        return;
    }

    const el = document.getElementById(id);
    if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
}

const HeroWithImage = (props: IHeroProps) => {
    const styles = useStyles();
    
    const heroClass = mergeClasses(styles.hero, styles.heroOverlay);
    const heroBgClass = mergeStyles(
        heroClass,
        {
            '::before': {
                backgroundImage: `url(${props.imageUri})`,
            }
        }
    );

    return (
        <section className={heroBgClass}>
            <div className={styles.heroContentWrapper}>
                <div className={styles.heroRow}>
                    {
                        props.clientLogos && props.clientLogos.length > 0 &&
                        <HeroClientLogoList logos={props.clientLogos} />
                    }

                    <div className={styles.content}>
                        <LargeTitle block as="h1" className={styles.title}>
                            {props.title}
                        </LargeTitle>
                        <Text block as="h3" className={styles.description} size={500}>
                            {props.subtitle}
                        </Text>
                        
                        <div className={styles.ctaContainer}>
                            <CallsToAction primaryCta={props.cta} secondaryCta={props.secondaryCta} />
                        </div>
                    </div>
                </div>
            </div>

            {
                props.showScrollHint !== false &&
                <ScrollDownButton onClick={() => scrollToId(props.scrollTargetId)} />
            }
        </section>
    );
};

const HeroWithVideo = (props: IHeroProps) => {
    const styles = useStyles();

    return (
        <div className={styles.hero}>
            <div className={styles.videoContainer}>
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className={`${styles.videoPlayer} ${styles.backgroundVideo}`}
                    poster={props.videoPreviewUri}
                    preload="none"
                    controls={false}
                >
                    <source type="video/mp4" src={props.videoUri} />
                </video>
            </div>
            <div className={styles.heroContentWrapper}>
                <div className={styles.heroRow}>
                    
                    {
                        props.clientLogos && props.clientLogos.length > 0 &&
                        <div className={styles.clientLogos}>
                            <HeroClientLogoList logos={props.clientLogos} />
                        </div>
                    }

                    <div className={styles.content}>
                        <LargeTitle as="h1" align="start" block className={styles.title}>
                            <span dangerouslySetInnerHTML={{ __html: props.title }} />
                        </LargeTitle>
                        <Text
                            as="h3"
                            size={500}
                            align="start"
                            block
                            className={styles.description}
                            style={{ marginBottom: "1.5rem", whiteSpace: "pre-line" }}
                        >
                            {props.subtitle}
                        </Text>

                        <div className={styles.ctaContainer}>
                            <CallsToAction primaryCta={props.cta} secondaryCta={props.secondaryCta} />
                        </div>
                    </div>
                </div>
            </div>

            {
                props.showScrollHint !== false &&
                <ScrollDownButton onClick={() => scrollToId(props.scrollTargetId)} />
            }
        </div>
    );
};

export default function Hero(props: IHeroProps) {
    useStyles(); 

    if (!!props.imageUri && !!props.videoUri) {
        throw new Error("Hero component requires either an image or a video URI, but not both.");
    }

    if (!!props.imageUri) {
        return <HeroWithImage {...props} />;
    }

    if (!!props.videoUri && !props.videoPreviewUri) {
        throw new Error("Hero component requires a video preview URI when using a video URI.");
    }

    if (!!props.videoUri) {
        return <HeroWithVideo {...props} />;
    }

    throw new Error("Hero component requires either an image or a video URI.");
}
